<template>
  <div
    v-if="showComponent"
    class="form-group mb-0">

    <div class="text-right mb-3 px-3 pt-3">
      <d-button
        theme="success"
        :disabled="isSaving || !allDataLoaded || load_error"
        :class="{ 'isLoading': isSaving }"
        @click.prevent="saveTemplate">
        <i class="material-icons">save</i> Zapisz szablon
      </d-button>
    </div>

    <div v-if="allDataLoaded && blocksReady && !load_error">
      <drag-items
        ref="drag-items"
        :data="blocks"
        :stages="activeSuppliers"
        :suppliers="activeSuppliers"
        :refresh="loadFilteredData"
        :source="blocks"
        id-prop="id"
        status-prop="supplier_id"
        :unassigned-value="0"
        :auto-save="false" />
    </div>

    <div
      v-if="(!allDataLoaded || !blocksReady) && !load_error"
      class="pb-5 pt-3 text-center text-muted">
      <div
        class="spinner-border spinner-border-sm text-primary mr-2"
        role="status" />
      Trwa ładowanie danych&hellip;
    </div>

    <d-alert
      v-if="load_error"
      show
      theme="warning"
      class="mt-3 mx-3">
      Wczytywanie danych nie powiodło się. <a
        href="javascript:window.location.reload();"
      class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
    </d-alert>
  </div>
</template>

<script>
import Vue from 'vue';
import ListUtils from '@/utils/ListUtils';
import FormUtils from '@/utils/FormUtils';
import DragItems from '@/components/others/dragItems';

export default {
  name: 'route-templates-transport',
  components: { DragItems },
  props: {
    day: {
      required: true,
      type: String
    },
    showComponent: {
      default: true,
      type: Boolean
    }
  },
  data () {
    return {
      blocks: [],
      blocksReady: false,
      data: [],
      isSaving: false,
      load_error: false,
      loaded: false,
      loaded_shops: false,
      loaded_suppliers: false,
      shops: [],
      suppliers: []
    };
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loaded_suppliers && this.loaded_shops;
    },
    activeSuppliers () {
      return this.suppliers.filter(supplier => supplier.active === 1);
    }
  },
  watch: {
    day () {
      this.loadFilteredData();
    },
    suppliers (items) {
      if (items.length) {
        this.suppliersSort();
      }
    },
    allDataLoaded (isReady) {
      if (isReady) {
        this.blocksSort();
      }
    }
  },
  mounted () {
    this.loadSuppliers();
    this.loadShops();
    this.loadFilteredData();
  },
  methods: {
    loadSuppliers () {
      Vue.set(this, 'loaded_suppliers', false);

      FormUtils.loadAdditionalData(this, {
        endpoint: '/suppliers/items',
        params: {
          pagination: false
        },
        outputKey: 'suppliers',
        loadedKey: 'loaded_suppliers',
        errorKey: 'load_error'
      });
    },
    loadShops () {
      Vue.set(this, 'loaded_shops', false);

      FormUtils.loadAdditionalData(this, {
        endpoint: '/customers/shops/items',
        params: {
          pagination: false,
          where: { customer_is_archived: 0 }
        },
        outputKey: 'shops',
        loadedKey: 'loaded_shops',
        errorKey: 'load_error'
      });
    },
    suppliersSort () {
      const array = JSON.parse(JSON.stringify(this.suppliers));

      if (!array.find(s => s.id === 0)) {
        array.unshift({
          id: 0,
          name: 'Placówki',
          active: 1
        });
      }

      if (!this.suppliers.find(s => s.id === 0)) {
        this.suppliers = array;
      }
    },
    loadFilteredData () {
      this.blocksReady = false;
      this.blocks = [];
      this.data = [];
      this.loaded = false;
      this.load_error = false;

      ListUtils.loadItemsData(this, {
        endpoint: '/route-plan-template/items',
        listField: 'data',
        loadedKey: 'loaded',
        errorKey: 'load_error',
        pagination: false,
        params: {
          day: this.day
        }
      });
    },
    blocksSort () {
      let newBlocks = [];
      let assignedShopIds = new Set();

      if (this.data && this.data.length > 0) {
        this.data.forEach((item, index) => {
          assignedShopIds.add(item.shop_id);

          newBlocks.push({
            id: item.id || `template_${item.shop_id}`,
            ordering: item.ordering !== undefined ? item.ordering : index,
            supplier_id: item.supplier_id || 0,
            shop_id: item.shop_id,
            shop_name: item.shop_name,
            shop_address: item.shop_address,
            shop_city: item.shop_city
          });
        });
      }

      let unassignedCounter = newBlocks.length;
      if (this.shops && this.shops.length > 0) {
        this.shops.forEach(shop => {
          if (!assignedShopIds.has(shop.id)) {
            newBlocks.push({
              id: `shop_${shop.id}`,
              ordering: unassignedCounter++,
              supplier_id: 0,
              shop_id: shop.id,
              shop_name: shop.name,
              shop_address: shop.address,
              shop_city: shop.city
            });
          }
        });
      }

      this.blocks = newBlocks;
      this.blocksReady = true;
    },
    saveTemplate () {
      this.isSaving = true;

      const dragComponent = this.$refs['drag-items'];
      if (!dragComponent) {
        this.isSaving = false;
        return;
      }

      const currentDragState = dragComponent.prepareFormDataToSend();

      const payloadItems = currentDragState.items
        .filter(block => Number(block.supplier_id) !== 0)
        .map((block, index) => ({
          shop_id: block.shop_id,
          supplier_id: Number(block.supplier_id),
          ordering: index
      }));

      const payload = {
        day: this.day,
        items: payloadItems
      };

      FormUtils.submit(this, {
        endpoint: '/route-plan-template/save',
        formData: payload,
        successTitle: 'Zapisano szablon trasy',
        successText: 'Szablon na wybrany dzień został zapisany',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Modyfikacja szablonu trasy nie powiodła się. Spróbuj ponownie.',
        successAction: () => {
          this.isSaving = false;
          this.loadFilteredData();
        },
        failureAction: () => {
          this.isSaving = false;
        }
      });
    }
  }
};
</script>
